import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Flex } from './Flex.js';
import { cssVar } from '../theme/variables.js';
import * as Portal from '@radix-ui/react-portal';
import { Slot } from '@radix-ui/react-slot';
import { useState } from 'react';
import { css, styled } from 'styled-components';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { mediaHover } from '../constants/styled-utils.js';

const variants = {
  "bg-primary": css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-primary"], cssVar["color-text-primary"]),
  "bg-tertiary": css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-tertiary"], cssVar["color-text-primary"]),
  "bg-primary-reverse": css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-primary-reverse"], cssVar["color-bg-primary"]),
  "app-delete": css([
    `background-color:`,
    `;color:`,
    `;`
  ], cssVar["color-bg-delete"], cssVar["color-app-delete"]),
  "app-primary": css([
    `background-color:rgba(`,
    `,0.05);color:`,
    `;`
  ], cssVar["color-app-primary-rgb"], cssVar["color-app-primary-text"])
};
const sizes = {
  small: css([
    `font-size:10px;padding:0.125rem 0.25rem;border-radius:0.5rem;`
  ]),
  "small+": css([
    `font-size:12px;padding:0.125rem 0.188rem;border-radius:0.375rem;`
  ]),
  medium: css([
    `font-size:12px;padding:0.25rem 0.5rem;border-radius:0.75rem;`
  ]),
  large: css([
    `font-size:12px;font-weight:600;padding:0.375rem 0.635rem;border-radius:0.635rem;`
  ])
};
const positionBadge = (anchorOrigin) => {
  if (anchorOrigin === null) {
    return;
  }
  const vertical = anchorOrigin?.vertical ?? "top";
  const horizontal = anchorOrigin?.horizontal ?? "right";
  return css([
    ``,
    `:0;`,
    `:0;transform:translate( `,
    `,`,
    ` );`
  ], vertical, horizontal, horizontal === "right" ? "50%" : "-50%", vertical === "bottom" ? "50%" : "-50%");
};
const Badge = styled.span.withConfig({
  displayName: "Badge",
  componentId: "sc-eb433c0-0"
})([
  `display:inline-flex;align-items:center;justify-content:center;min-width:16px;font-weight:500;line-height:normal;user-select:none;position:absolute;z-index:1;`,
  ` `,
  ` `,
  ` `,
  ``
], (p) => variants[p.variant ?? "bg-primary"], (p) => sizes[p.size ?? "small"], (p) => positionBadge(p.anchorOrigin), (p) => p.clickable && css([
  ``,
  `{&:hover:not(:disabled){box-shadow:0 0 10rem 0 rgba(`,
  `,0.1) inset;}}cursor:pointer;user-select:none;&:active{box-shadow:0 0 10rem 0 rgba(`,
  `,0.1) inset;}`
], mediaHover, cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"]));
const StaticBadge = styled(Badge).attrs({
  anchorOrigin: null
}).withConfig({
  displayName: "Badge__StaticBadge",
  componentId: "sc-eb433c0-1"
})([
  `position:static;transform:none;`
]);
const BadgeContainer = styled(Flex).withConfig({
  displayName: "Badge__BadgeContainer",
  componentId: "sc-eb433c0-2"
})([
  `position:relative;`
]);
const FloatingBadge = ({ children, badgeContent, variant, anchorOrigin }) => {
  const { vertical = "top", horizontal = "right" } = anchorOrigin ?? {};
  const [element, setElement] = useState(null);
  const [rect] = useDomContentRect(element, {
    observeParentResize: true,
    includeRectPosition: true
  });
  const badgeStyle = {
    top: vertical === "top" ? rect?.top : rect?.bottom,
    left: horizontal === "left" ? rect?.left : rect?.right,
    transform: "translate(-50%, -50%)",
    right: "unset",
    bottom: "unset"
  };
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(Slot, {
        ref: setElement,
        children
      }),
      /* @__PURE__ */ jsx(Portal.Root, {
        asChild: true,
        container: getDefaultRadixPortalContainer(),
        children: /* @__PURE__ */ jsx(Badge, {
          variant,
          style: badgeStyle,
          children: badgeContent
        })
      })
    ]
  });
};
const Badge$1 = Object.assign(Badge, {
  Container: BadgeContainer,
  Floating: FloatingBadge,
  Static: StaticBadge
});

export { Badge$1 as default };
//# sourceMappingURL=Badge.js.map
